import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingCart, Plus, Minus, Trash, Banknote, CreditCard } from 'lucide-react';
import { useCart } from './CartContext';

const CartModal = ({ isOpen, onClose }) => {
  const { cartItems, updateQuantity, removeFromCart, getTotalPrice, clearCart } = useCart();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);

  if (!isOpen) return null;

  const handleProceedToPayment = () => {
    if (selectedPaymentMethod) {
      setShowPaymentDetails(true);
    } else {
      alert('Por favor, selecciona un método de pago.');
    }
  };

  const handleConfirmPurchase = () => {
    alert(`¡Compra confirmada con ${selectedPaymentMethod}! (Esto es solo una simulación, no te endeudes)`);
    clearCart();
    setShowPaymentDetails(false);
    setSelectedPaymentMethod('');
    onClose();
  };

  const paymentMethods = [
    { id: 'bank_deposit', name: 'Depósito en Banco', icon: Banknote },
    { id: 'bank_transfer', name: 'Transferencia Bancaria', icon: CreditCard },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-end z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="bg-white w-full max-w-md h-full shadow-2xl flex flex-col"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
                <ShoppingCart className="w-7 h-7 text-purple-600" />
                Tu Carrito
              </h2>
              <motion.button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <X className="w-6 h-6 text-gray-600" />
              </motion.button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {cartItems.length === 0 ? (
                <motion.div
                  className="text-center py-10 text-gray-500"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <ShoppingCart className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">Tu carrito está vacío. ¡Es hora de comprar!</p>
                </motion.div>
              ) : (
                <>
                  <AnimatePresence>
                    {cartItems.map((item) => (
                      <motion.div
                        key={item.id}
                        layout
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -50 }}
                        transition={{ duration: 0.3 }}
                        className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl shadow-sm border border-gray-100"
                      >
                        <img src={item.imageUrl} alt={item.name} className="w-20 h-20 object-cover rounded-lg" />
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-800">{item.name}</h3>
                          <p className="text-gray-600 text-sm">PEN {item.price.toFixed(2)}</p>
                          <div className="flex items-center mt-2">
                            <motion.button
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              className="p-1 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <Minus className="w-4 h-4 text-gray-700" />
                            </motion.button>
                            <span className="mx-2 font-medium text-gray-800">{item.quantity}</span>
                            <motion.button
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              className="p-1 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <Plus className="w-4 h-4 text-gray-700" />
                            </motion.button>
                          </div>
                        </div>
                        <motion.button
                          onClick={() => removeFromCart(item.id)}
                          className="p-2 rounded-full bg-red-100 hover:bg-red-200 transition-colors"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <Trash className="w-5 h-5 text-red-600" />
                        </motion.button>
                      </motion.div>
                    ))}
                  </AnimatePresence>

                  <div className="mt-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-3">Método de Pago</h3>
                    <div className="space-y-3">
                      {paymentMethods.map((method) => (
                        <motion.label
                          key={method.id}
                          className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all duration-200 ${
                            selectedPaymentMethod === method.id
                              ? 'border-purple-500 bg-purple-50 shadow-md'
                              : 'border-gray-200 bg-white hover:bg-gray-50'
                          }`}
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.99 }}
                        >
                          <input
                            type="radio"
                            name="paymentMethod"
                            value={method.id}
                            checked={selectedPaymentMethod === method.id}
                            onChange={(e) => {
                              setSelectedPaymentMethod(e.target.value);
                              setShowPaymentDetails(false); // Oculta detalles si cambia de método
                            }}
                            className="form-radio h-5 w-5 text-purple-600"
                          />
                          <method.icon className="w-6 h-6 text-gray-700" />
                          <span className="font-medium text-gray-800">{method.name}</span>
                        </motion.label>
                      ))}
                    </div>
                  </div>

                  {showPaymentDetails && selectedPaymentMethod === 'bank_deposit' && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200 text-blue-800"
                    >
                      <h4 className="font-bold mb-2">Detalles para Depósito en Banco:</h4>
                      <p>Banco: Banco Nerd</p>
                      <p>Número de Cuenta: 123-456789-0-123</p>
                      <p>Titular: Massi Store S.A.C.</p>
                      <p className="mt-2 text-sm">Por favor, envía el comprobante a nuestro correo después de realizar el depósito.</p>
                    </motion.div>
                  )}

                  {showPaymentDetails && selectedPaymentMethod === 'bank_transfer' && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-4 p-4 bg-green-50 rounded-xl border border-green-200 text-green-800"
                    >
                      <h4 className="font-bold mb-2">Detalles para Transferencia Bancaria:</h4>
                      <p>Banco: Banco Nerd</p>
                      <p>CCI: 001-123-4567890123-45</p>
                      <p>Titular: Massi Store S.A.C.</p>
                      <p className="mt-2 text-sm">No olvides incluir el número de tu pedido en la descripción de la transferencia.</p>
                    </motion.div>
                  )}
                </>
              )}
            </div>

            <div className="p-6 border-t border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <span className="text-xl font-bold text-gray-800">Total:</span>
                <span className="text-2xl font-extrabold text-purple-600">PEN {getTotalPrice().toFixed(2)}</span>
              </div>
              {!showPaymentDetails ? (
                <motion.button
                  onClick={handleProceedToPayment}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={cartItems.length === 0 || !selectedPaymentMethod}
                >
                  Proceder al Pago
                </motion.button>
              ) : (
                <motion.button
                  onClick={handleConfirmPurchase}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Confirmar Compra
                </motion.button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CartModal;